import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useAsyncReport } from '@/hooks/reports/useReportQuery'
import {
  resolveComparison,
  toIsoBounds,
  type DashboardPeriod,
} from '@/lib/dashboardPeriod'

export type DashboardSummaryTotals = {
  won_count: number
  won_value: number
  lost_count: number
  avg_ticket: number
  conversion_rate: number
  new_leads: number
}

export type DashboardMetrics = {
  summary: DashboardSummaryTotals & { prev: DashboardSummaryTotals }
  daily: { day: string; won_count: number; won_value: number }[]
  won_by_user: {
    user_id: string | null
    name: string
    count: number
    value: number
    pct: number
  }[]
  lost_by_user: { user_id: string | null; name: string; count: number; pct: number }[]
  loss_reasons: { reason_id: string | null; name: string; count: number; pct: number }[]
  period_flow: { opened: number; won: number; lost: number }
}

export type DashboardStage = {
  stage_id: string
  name: string
  order_index: number
  count: number
  value: number
  pct_of_value: number
  avg_idle_days: number | null
}

export type DashboardPipelineFunnel = {
  pipeline_id: string
  name: string
  /** Whole life of this funnel, all statuses */
  total_count: number
  open_status_count: number
  won_count: number
  lost_count: number
  /** Open deals sitting in normal stages, matching the sum of `stages` */
  open_count: number
  open_value: number
  stages: DashboardStage[]
}

export type DashboardSnapshot = {
  /** Null when the filter is on every funnel */
  pipeline: { id: string; name: string } | null
  /** Whole life of the selected scope, all statuses */
  pipeline_total: { count: number; open: number; won: number; lost: number }
  open_total: { count: number; value: number; count_with_price: number }
  /** One entry per funnel in scope: a single one, or every active funnel */
  pipelines: DashboardPipelineFunnel[]
  operation: {
    open_chats_total: number
    unassigned: number
    awaiting_reply: number
    stale_hours: number
    by_user: { user_id: string; name: string; count: number }[]
  }
}

const EMPTY_TOTALS: DashboardSummaryTotals = {
  won_count: 0,
  won_value: 0,
  lost_count: 0,
  avg_ticket: 0,
  conversion_rate: 0,
  new_leads: 0,
}

export const EMPTY_DASHBOARD_METRICS: DashboardMetrics = {
  summary: { ...EMPTY_TOTALS, prev: { ...EMPTY_TOTALS } },
  daily: [],
  won_by_user: [],
  lost_by_user: [],
  loss_reasons: [],
  period_flow: { opened: 0, won: 0, lost: 0 },
}

export const EMPTY_DASHBOARD_SNAPSHOT: DashboardSnapshot = {
  pipeline: null,
  pipeline_total: { count: 0, open: 0, won: 0, lost: 0 },
  open_total: { count: 0, value: 0, count_with_price: 0 },
  pipelines: [],
  operation: {
    open_chats_total: 0,
    unassigned: 0,
    awaiting_reply: 0,
    stale_hours: 4,
    by_user: [],
  },
}

const BROWSER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'
  } catch {
    return 'America/Sao_Paulo'
  }
})()

/**
 * Period-based results. The comparison window is computed here and sent explicitly,
 * because only the preset knows what a fair comparison is (see resolveComparison).
 */
export function useDashboardCrmMetrics(period: DashboardPeriod, pipelineId: string | null) {
  const { user } = useAuth()
  const accountId = user?.account_id || user?.account?.id || null

  const { fromIso, toIso } = toIsoBounds(period.from, period.to)
  const comparison = resolveComparison(period)
  const { fromIso: prevFromIso, toIso: prevToIso } = toIsoBounds(comparison.from, comparison.to)

  const query = useAsyncReport<DashboardMetrics>(
    async () => {
      if (!supabase) throw new Error('Supabase não está configurado')
      if (!accountId) return EMPTY_DASHBOARD_METRICS

      const { data, error } = await supabase.rpc('get_dashboard_crm_metrics', {
        p_from: fromIso,
        p_to: toIso,
        p_prev_from: prevFromIso,
        p_prev_to: prevToIso,
        p_pipeline_id: pipelineId,
        p_timezone: BROWSER_TIMEZONE,
      })

      if (error) throw new Error(error.message)

      return {
        ...EMPTY_DASHBOARD_METRICS,
        ...((data as DashboardMetrics | null) ?? {}),
      }
    },
    EMPTY_DASHBOARD_METRICS,
    [
      accountId,
      fromIso,
      toIso,
      prevFromIso,
      prevToIso,
      pipelineId,
      user?.auth_user_id,
      user?.view_all_contacts,
      user?.view_unassigned_contacts,
    ],
  )

  return { ...query, comparison }
}

/**
 * Current position. Not period-filtered on purpose: a funnel answers "where is the
 * money right now", so it must not shrink or grow when the period filter changes.
 */
export function useDashboardCrmSnapshot(pipelineId: string | null, staleHours = 4) {
  const { user } = useAuth()
  const accountId = user?.account_id || user?.account?.id || null

  return useAsyncReport<DashboardSnapshot>(
    async () => {
      if (!supabase) throw new Error('Supabase não está configurado')
      if (!accountId) return EMPTY_DASHBOARD_SNAPSHOT

      const { data, error } = await supabase.rpc('get_dashboard_crm_snapshot', {
        p_pipeline_id: pipelineId,
        p_stale_hours: staleHours,
      })

      if (error) throw new Error(error.message)

      return {
        ...EMPTY_DASHBOARD_SNAPSHOT,
        ...((data as DashboardSnapshot | null) ?? {}),
      }
    },
    EMPTY_DASHBOARD_SNAPSHOT,
    [
      accountId,
      pipelineId,
      staleHours,
      user?.auth_user_id,
      user?.view_all_contacts,
      user?.view_unassigned_contacts,
    ],
  )
}
